import React from 'react';
import PropTypes from 'prop-types';
import PageHeader from './PageHeader';
import { useColors } from '../../../hooks/useColors';

/**
 * Unified page layout component that encapsulates container, page header, and content area
 * Ensures consistency across all pages
 * 
 * @param {Object} props - Component props
 * @param {string} props.title - Page title (centered)
 * @param {Array} [props.breadcrumbs] - Optional array of breadcrumb items with label and path
 * @param {React.ReactNode} [props.buttons] - Optional action buttons (displayed on the right)
 * @param {boolean} [props.showTitle=true] - Whether to display the title
 * @param {string} [props.className] - Additional CSS classes for container
 * @param {string} [props.contentClassName] - Additional CSS classes for content wrapper
 * @param {boolean} [props.wrapContent=false] - Wrap content in semantic content card
 * @param {string} [props.contentCardClassName] - Custom classes for content card
 * @param {React.ReactNode} props.children - Page content
 */
const PageLayout = ({
  title,
  breadcrumbs,
  buttons,
  showTitle = true,
  className = '',
  contentClassName = '',
  wrapContent = false,
  contentCardClassName = '',
  children
}) => {
  const { semantic } = useColors();

  const containerClasses = `container mx-auto mb-6 ${className}`.trim();
  const contentClasses = wrapContent
    ? `${semantic.contentCardBg} rounded-lg p-6 ${contentCardClassName} ${contentClassName}`.trim()
    : contentClassName;

  return (
    <div className={containerClasses}>
      <PageHeader
        title={title}
        breadcrumbs={breadcrumbs}
        buttons={buttons}
        showTitle={showTitle}
      />
      <div className={contentClasses}>
        {children}
      </div>
    </div>
  );
};

PageLayout.propTypes = {
  title: PropTypes.string,
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      path: PropTypes.string.isRequired
    })
  ),
  buttons: PropTypes.node,
  showTitle: PropTypes.bool,
  className: PropTypes.string,
  contentClassName: PropTypes.string,
  wrapContent: PropTypes.bool,
  contentCardClassName: PropTypes.string,
  children: PropTypes.node.isRequired
};

export default PageLayout;

